import axios from 'axios';
import Env from '../env.json';
import { getAnonymousUUID, getAuthToken, STORAGE_KEYS } from 'utils/storage.utils';

// Utility function to generate query strings
export const querystring = (query = {}) => {
    return Object.entries(query)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
};

// Create an Axios instance with a base URL
const axiosInstance = axios.create({
    baseURL: Env.BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

// Add a request interceptor to include the auth token

axiosInstance.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        const uuid = getAnonymousUUID();
        if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
        } else if (uuid) {
            config.headers["x-anonymous-uuid"] = uuid;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Add a response interceptor to handle errors globally and sanitize responses
axiosInstance.interceptors.response.use(
    (response) => {
        // Remove sensitive headers from response (if any leaked through)
        if (response.headers) {
            delete response.headers['x-powered-by'];
            delete response.headers['server'];
            delete response.headers['x-runtime'];
            delete response.headers['x-request-id'];
        }
        return response;
    },
    (error) => {
        // Sanitize error responses to hide backend details
        if (error.response) {
            // Remove sensitive headers
            if (error.response.headers) {
                delete error.response.headers['x-powered-by'];
                delete error.response.headers['server'];
                delete error.response.headers['x-runtime'];
                delete error.response.headers['x-request-id'];
            }
            
            // Sanitize error data in production
            if (process.env.NODE_ENV === 'production' && error.response.data) {
                // Don't expose stack traces or internal error details
                if (error.response.data.stack) {
                    delete error.response.data.stack;
                }
                if (error.response.data.error?.stack) {
                    delete error.response.data.error.stack;
                }
                // Generic error message for non-operational errors
                if (error.response.status >= 500 && error.response.data.error) {
                    error.response.data = {
                        status: 'error',
                        message: error.response.data.message || 'An error occurred. Please try again later.',
                        statusCode: error.response.status
                    };
                }
            }
        }
        
        if (error.response?.status === 401) {
            localStorage.removeItem(STORAGE_KEYS.AUTH_USER_DETAILS);
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);


// Function to handle GET requests
export const getRequest = async (url, params = {}) => {
    const query = querystring(params);
    const response = await axiosInstance.get(`${url}${query ? `?${query}` : ''}`);
    return response.data;
};

// Function to handle POST requests
export const postRequest = async (url, data = {}) => {
    const response = await axiosInstance.post(url, data);
    return response.data;
};

// Function to handle form data uploads
export const uploadFiles = async (url, files, params = {}) => {
    const formData = generateFormData(files, params);
    const response = await axiosInstance.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

// Utility to generate FormData for file uploads
export const generateFormData = (files, body = {}) => {
    const formData = new FormData();
    if (Array.isArray(files)) {
        files.forEach((file) => formData.append('files', file));
    } else if (files) {
        formData.append('file', files);
    }
    Object.keys(body).forEach((key) => formData.append(key, body[key]));
    return formData;
};

export default axiosInstance;